import { useState } from 'react';
import { Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MapTimelineLayer,
  MapTimelineLayersDetails,
  TimelineLayerId,
} from 'features/mapping/mapping.constants';
import { TimelineLayerMenuButton } from './TimelineLayerMenuButton';
import { TimelineLayerMenuList } from './TimelineLayerMenuList';

type TimelineLayerMenuProps = {
  layers: MapTimelineLayer[];
  selectedLayerId: TimelineLayerId;
  onChange: (layerId: TimelineLayerId) => void;
};

/**
 * Wraps a {@link TimelineLayerMenuList} in a Popover anchored to a
 * {@link TimelineLayerMenuButton}.
 */
export const TimelineLayerMenu = ({
  layers,
  selectedLayerId,
  onChange,
}: TimelineLayerMenuProps): JSX.Element => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedLayer = MapTimelineLayersDetails[selectedLayerId];

  const handleSelect = (layerId: TimelineLayerId): void => {
    setAnchorEl(null);
    if (layerId !== selectedLayerId) {
      onChange(layerId);
    }
  };

  return (
    <>
      <TimelineLayerMenuButton
        title={t(selectedLayer.titleKey)}
        open={Boolean(anchorEl)}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'background.secondary',
              backgroundImage: 'none',
              boxSizing: 'border-box',
              width: 350,
              borderRadius: 2,
              padding: 1,
            },
          },
        }}
      >
        <TimelineLayerMenuList
          layers={layers}
          selectedLayerId={selectedLayerId}
          onSelect={handleSelect}
        />
      </Popover>
    </>
  );
};
